import {
  ReactNode,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
import { LicenseInfo } from "@mui/x-license";

import { ApiContext } from "./context";
import { ApiService } from "../../services/api/service";
import { AppType } from "./type";
import { AuthenticationService as BgwAuthenticationService } from "../../services/api/authentication";
import { AuthenticationService as PAuthenticationService } from "../../services/perspective_api/authentication";
import { PerspectiveApiService } from "../../services/perspective_api/service";
import { SysMessageLevel } from "../sys_messages/type";
import { SysMessagesContext } from "../sys_messages/context";
import { ROOT_ROLE, ADMIN_ROLE } from "../../constants/user";
import {
  useGetAllCustomersQuery,
  useGetMyCustomerQuery,
  useWhoAmIQuery,
} from "../../services/api/__generated__/backend_gateway-types";
import { IdTokenContext } from "../authentication/amplify";
import initWasm from "parquet-wasm";

const { Provider } = ApiContext;

export const AppProvider = (props: { children: ReactNode }): JSX.Element => {
  const isConnectingRef = useRef(false);
  const isCreatingNewConnectionRef = useRef(false);
  const { addSystemMessage } = useContext(SysMessagesContext);

  // Set default customer ID for atomic url logins
  if (!localStorage.getItem("selectedCustomerId")) {
    localStorage.setItem("selectedCustomerId", "glowco");
  }

  const [appState, setAppState] = useState({
    selectedCustomerId: localStorage.getItem("selectedCustomerId"),
    apiService: ApiService.getInstance(),
    perspectiveApiService: PerspectiveApiService.getInstance(),
    licensesRegistered: false,
  });

  const [demoMode, setDemoMode] = useState<boolean>(false);

  // WASM state
  const [wasmInitialized, setWasmInitialized] = useState(false);
  const [wasmError, setWasmError] = useState<string | null>(null);

  // Reusable error handler for GraphQL queries
  const handleQueryError = useCallback(
    (error: any, queryName: string) => {
      // Don't show warnings for network/CORS errors - these are expected in some environments
      if (
        error?.message &&
        !error.message.includes("Failed to fetch") &&
        !error.message.includes("CORS")
      ) {
        addSystemMessage({
          level: SysMessageLevel.WARNING,
          message: error.message,
        });
      }
      console.warn(
        `${queryName}: fetch error (backend gateway may be unavailable)`,
        error
      );
    },
    [addSystemMessage]
  );

  // GraphQL queries
  const { data: whoIAmData } = useWhoAmIQuery({
    client: appState.apiService.getClient(),
    onError: (error) => handleQueryError(error, "useWhoAmIQuery"),
    skip: !appState.apiService,
    errorPolicy: "ignore", // Don't fail the entire app if this query fails
  });

  const { data: customerSettings, refetch: refetchCustomerSettings } =
    useGetMyCustomerQuery({
      client: appState.apiService.getClient(),
      skip: !appState.apiService,
      errorPolicy: "ignore", // Don't fail the entire app if this query fails
      fetchPolicy: "cache-and-network", // Ensure we get fresh data
    });

  const { data: getAllCompaniesData } = useGetAllCustomersQuery({
    client: appState.apiService.getClient(),
    onError: (error) => handleQueryError(error, "useGetAllCustomersQuery"),
    skip: !appState.apiService,
    errorPolicy: "ignore", // Don't fail the entire app if this query fails
  });

  // Connection management functions
  const connect = useCallback(async () => {
    if (isCreatingNewConnectionRef.current) {
      console.info("Already creating a new connection, skipping...");
      return;
    }

    try {
      isCreatingNewConnectionRef.current = true;
      // Reset Apollo clients to retrigger state updates
      setAppState((prev) => ({
        ...prev,
        apiService: ApiService.getInstance(),
        perspectiveApiService: PerspectiveApiService.getInstance(),
      }));
      isConnectingRef.current = false;
      isCreatingNewConnectionRef.current = false;
    } catch (error) {
      console.error("Error establishing connection:", error);
      isCreatingNewConnectionRef.current = false;
    }
  }, []);

  const reconnectApis = useCallback(() => {
    appState.perspectiveApiService.reset();
    appState.apiService.reset();
    connect();
  }, [appState.perspectiveApiService, appState.apiService, connect]);

  const stopApis = useCallback(() => {
    appState.perspectiveApiService.stop();
    appState.apiService.stop();
  }, [appState.perspectiveApiService, appState.apiService]);

  const setSelectedCompanyId = useCallback(
    (selectedCustomerId: string | null): void => {
      if (
        selectedCustomerId &&
        appState.selectedCustomerId !== selectedCustomerId
      ) {
        const resetNeeded = appState.selectedCustomerId !== null;
        localStorage.setItem("selectedCustomerId", selectedCustomerId);
        if (resetNeeded) {
          reconnectApis();
        }
      } else if (selectedCustomerId === null) {
        localStorage.removeItem("selectedCustomerId");
        stopApis();
      }
      setAppState((prev) => ({ ...prev, selectedCustomerId }));
    },
    [appState.selectedCustomerId, reconnectApis, stopApis]
  );

  const setIdToken = useCallback((idToken: string | undefined) => {
    BgwAuthenticationService.setIdToken(idToken);
    PAuthenticationService.setIdToken(idToken);
  }, []);

  // Handle MUI license registration
  if (
    customerSettings?.getMyCustomer?.muiLicense &&
    !appState.licensesRegistered
  ) {
    try {
      LicenseInfo.setLicenseKey(customerSettings?.getMyCustomer?.muiLicense);
      setAppState((prev) => ({ ...prev, licensesRegistered: true }));
    } catch (e) {
      console.error("Failed to register licenses", e);
    }
  }

  // Initialize connection on mount
  useEffect(() => {
    connect();
  }, [connect]);

  // Handle ID token changes
  const { idToken } = useContext(IdTokenContext);
  const lastIdTokenRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (lastIdTokenRef.current !== idToken) {
      setIdToken(idToken);
      if (lastIdTokenRef.current !== undefined) {
        reconnectApis();
      } else {
        stopApis();
      }
    }
    lastIdTokenRef.current = idToken;
  }, [idToken, setIdToken, reconnectApis, stopApis]);

  // Initialize WASM globally
  useEffect(() => {
    const initWasmModule = async () => {
      try {
        await initWasm();
        setWasmInitialized(true);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        setWasmError(errorMessage);
        console.error("Failed to initialize WASM globally:", error);
      }
    };

    initWasmModule();
  }, []);

  // Calculate effective role and admin status
  const actualRole = whoIAmData?.whoAmI?.role || null;
  const isRootUser = actualRole?.includes(ROOT_ROLE) ?? false;

  // In demo mode, ROOT users appear as ADMIN
  const effectiveRole = isRootUser && demoMode ? ADMIN_ROLE : actualRole;
  const effectiveIsAdmin = isRootUser && !demoMode; // Only true if ROOT and demo mode is off

  // Create context value
  const value: AppType = useMemo(
    () => ({
      // API services
      apiService: appState.apiService,
      perspectiveApiService: appState.perspectiveApiService,

      // Customer management
      selectedCustomerId: appState.selectedCustomerId,
      setSelectedCustomerId: setSelectedCompanyId,
      customerSettings: customerSettings?.getMyCustomer || null,
      lockedRun: customerSettings?.getMyCustomer?.forecastLockedToRunId || null,
      refetchCustomerSettings,

      // User and admin state
      whoIAm: whoIAmData?.whoAmI
        ? {
            ...whoIAmData.whoAmI,
            role: effectiveRole, // Override role for demo mode display
          }
        : null,
      actualRole: actualRole, // Store actual role for internal logic (e.g., customer selection)
      allCustomers: getAllCompaniesData?.getAllCustomers || [],
      isAdmin: effectiveIsAdmin,

      // Demo mode state
      demoMode,
      setDemoMode,

      // App state
      loading: isConnectingRef.current,
      licensesRegistered: appState.licensesRegistered,

      // WASM state
      wasmInitialized,
      wasmError,
    }),
    [
      appState.apiService,
      appState.perspectiveApiService,
      appState.selectedCustomerId,
      appState.licensesRegistered,
      setSelectedCompanyId,
      customerSettings?.getMyCustomer,
      refetchCustomerSettings,
      whoIAmData?.whoAmI,
      effectiveRole,
      actualRole,
      getAllCompaniesData?.getAllCustomers,
      effectiveIsAdmin,
      demoMode,
      wasmInitialized,
      wasmError,
    ]
  );

  return <Provider value={value}>{props.children}</Provider>;
};

// Custom hooks for accessing context data
export const useAppLoading = () => {
  const { loading } = useContext(ApiContext);
  return loading;
};

export const useAdmin = () => {
  const { allCustomers, isAdmin, loading } = useContext(ApiContext);
  return { allCustomers, isAdmin, loading };
};

export const useApi = () => {
  const { apiService, selectedCustomerId, setSelectedCustomerId } =
    useContext(ApiContext);
  return { apiService, selectedCustomerId, setSelectedCustomerId };
};

export const usePerspective = () => {
  const { perspectiveApiService } = useContext(ApiContext);
  return { perspectiveApiService };
};

export const useCustomer = () => {
  const {
    customerSettings,
    loading,
    licensesRegistered,
    whoIAm,
    selectedCustomerId,
    lockedRun,
  } = useContext(ApiContext);
  return {
    customerSettings,
    loading,
    licensesRegistered,
    whoIAm,
    selectedCustomerId,
    lockedRun,
  };
};

export const useWasm = () => {
  const { wasmInitialized, wasmError } = useContext(ApiContext);
  return { wasmInitialized, wasmError };
};

export const useLockedRun = () => {
  const { lockedRun } = useContext(ApiContext);
  return { lockedRun, hasLockedRun: !!lockedRun };
};

export const useRefetchCustomerSettings = () => {
  const { refetchCustomerSettings } = useContext(ApiContext);
  return { refetchCustomerSettings };
};
